import React, { FC, useState } from 'react';
import {
  Alert,
  Button,
  DropdownMenu,
  Icon,
  IconButton,
  mergeTailwindClasses,
} from '@kontentino/ui';
import { SchedulingInformation } from 'types/PostDetail';
import { faEllipsisV } from '@fortawesome/pro-regular-svg-icons/faEllipsisV';
import { faCopy } from '@fortawesome/pro-regular-svg-icons/faCopy';
import { faPen } from '@fortawesome/pro-regular-svg-icons/faPen';
import { faEye } from '@fortawesome/pro-regular-svg-icons/faEye';
import { faLink } from '@fortawesome/pro-regular-svg-icons/faLink';
import { createSearchFromParams, getBasePath, openBlank } from 'utils/url';
import useCopyToClipboard from 'app/hooks/useCopyToClipboard';
import UserPermissionGate from 'components/UserPermissionGate';
import { UserPermission } from 'constants/userPermission';
import ChangeUserToRetrieveSchedulePushNotificationModal from 'components/posts/detail/overview/ChangeUserToRetrieveSchedulePushNotificationModal';
import { TYPE_INSTAGRAM } from '@kontentino/kontentino-constants/Pages';
import PermissionsGateButton from 'app/components/PermissionsGateButton';
import clsx from 'clsx';
import { Post, PostPublishingMethod } from 'types/Post';
import { useTranslation } from 'react-i18next';
import PostTimeSaversApi, {
  ShiftToProjectParams,
} from 'app/api/postTimeSavers';
import { ApiClientError } from 'api/client';
import { useMutation } from 'react-query';
import { useToast } from 'app/hooks/useToast';
import { popupConfirm } from 'utils/popup';
import Modal from 'components/shared/Modal';
import Link from 'components/shared/Link';
import routes, { modalRoutes } from 'constants/routes';
import { usePostsLayoutState } from 'app/modules/posts/hooks/usePostsLayoutState';

type Props = {
  schedulingInfo: SchedulingInformation;
  postId: number;
  projectId: number;
  publishingMethod: PostPublishingMethod;
  page: {
    id: number;
    type: number;
  };
  onEditPost?: () => void;
};
const PostScheduledInformation: FC<Props> = ({
  schedulingInfo,
  postId,
  projectId,
  publishingMethod,
  page,
  onEditPost,
}) => {
  const copyUrl = useCopyToClipboard();
  const toast = useToast();
  const [isModalToChangeUserOpen, setOpenModalToChangeUser] = useState(false);
  const [isDuplicateSuccessOpen, setDuplicateSuccessOpen] = useState(false);
  const canShowChangeUserOption =
    publishingMethod === 'mobileApp' &&
    !!schedulingInfo.additional_data?.user &&
    page.type === TYPE_INSTAGRAM;
  const { t } = useTranslation();
  const isNewPostExperienceActive = usePostsLayoutState(
    (state) => state.isNewPostExperienceActive,
  );

  const duplicateAsDraft = useMutation<
    Post[],
    ApiClientError,
    ShiftToProjectParams
  >(PostTimeSaversApi.duplicatePosts, {
    onSuccess() {
      setDuplicateSuccessOpen(true);
    },
    onError(e: ApiClientError) {
      toast(e?.userMessage || t('somethingWentWrong'), 'error');
    },
  });

  const handleEditPost = async () => {
    const result = await popupConfirm({
      title: t('editPost', { defaultValue: 'Edit post' }),
      text: t('editPostedPostWarning', {
        defaultValue:
          "Editing this post will change status back to draft, any changes made won't be reflected in the version that was posted in your profile.",
      }),
      confirmButtonText: t('accept', { defaultValue: 'Accept' }),
      customClass: {
        popup: '!tw-w-[400px]',
      },
    });

    if (!result.isConfirmed) {
      return;
    }

    onEditPost?.();
  };

  const handleDuplicateAsDraft = () => {
    duplicateAsDraft.mutate({
      ids: [postId],
      projectId,
      variant: 'duplicate',
      cloneAsDraft: true,
    });
  };

  const duplicatedPostUrl = duplicateAsDraft.data?.[0]
    ? `${getBasePath()}${routes.POSTS_CALENDAR}${createSearchFromParams({
        id: duplicateAsDraft.data[0].id,
        profiles: page.id,
      })}#${
        isNewPostExperienceActive
          ? modalRoutes.POST_DETAIL_NEW
          : modalRoutes.POST_DETAIL
      }`
    : '';

  return (
    <>
      {schedulingInfo?.chosen_method === 'export' && (
        <Alert data-cy="post-scheduling-eported-message">
          {t('postScheduling.postExportedMessage')}
        </Alert>
      )}
      {schedulingInfo.type === 'link' && (
        <>
          <PermissionsGateButton
            scopes={UserPermission.POST_SEE_EXTERNAl}
            variant="secondary"
            data-cy="post-see-external-post-button"
            iconBefore={<Icon icon={faEye} />}
            onClick={() => openBlank(schedulingInfo.data)}
            className="tw-shrink-0"
          >
            {t('postScheduling.seePost')}
          </PermissionsGateButton>
          <PermissionsGateButton
            scopes={UserPermission.POST_COPY_LINK}
            variant="secondary"
            data-cy="post-copy-url-button"
            iconBefore={<Icon icon={faLink} />}
            onClick={() => copyUrl(schedulingInfo.data)}
            className="tw-shrink-0"
          >
            {t('postScheduling.copyUrl')}
          </PermissionsGateButton>
          <UserPermissionGate scopes={UserPermission.POST_EDIT}>
            <DropdownMenu.Root>
              <DropdownMenu.Trigger>
                <IconButton
                  variant="secondary"
                  icon={<Icon icon={faEllipsisV} />}
                  data-cy="post-posted-more-options-button"
                  className="tw-shrink-0"
                />
              </DropdownMenu.Trigger>
              <DropdownMenu.Content align="end">
                {onEditPost && (
                  <DropdownMenu.Item
                    data-cy="post-posted-edit-post"
                    onSelect={handleEditPost}
                  >
                    <Icon icon={faPen} className="tw-mr-2" />
                    {t('editPost', { defaultValue: 'Edit post' })}
                  </DropdownMenu.Item>
                )}
                <DropdownMenu.Item
                  data-cy="post-posted-duplicate-as-draft"
                  onSelect={handleDuplicateAsDraft}
                  disabled={duplicateAsDraft.isLoading}
                >
                  <Icon icon={faCopy} className="tw-mr-2" />
                  {t('duplicatePostAsDraft', {
                    defaultValue: 'Duplicate post as draft',
                  })}
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Root>
          </UserPermissionGate>
        </>
      )}
      {schedulingInfo.type === 'message' && (
        <>
          <Alert
            data-cy="post-scheduling-info-message"
            className="tw-flex tw-items-center"
          >
            <div
              className={mergeTailwindClasses(
                clsx('-tw-mt-[3px] tw-flex tw-items-center', {
                  '-tw-mt-1': canShowChangeUserOption,
                }),
              )}
            >
              <div className="tw-text-md tw-font-medium">
                {schedulingInfo.data}
              </div>
              {canShowChangeUserOption && (
                <UserPermissionGate
                  exact={false}
                  scopes={[UserPermission.POST_SCHEDULE]}
                >
                  <DropdownMenu.Root>
                    <DropdownMenu.Trigger>
                      <IconButton
                        icon={<Icon icon={faEllipsisV} />}
                        variant="ghost"
                        size="small"
                        className="tw-ml-2 tw-h-[24px]"
                      />
                    </DropdownMenu.Trigger>
                    <DropdownMenu.Content>
                      <UserPermissionGate scopes={UserPermission.POST_SCHEDULE}>
                        <DropdownMenu.Item
                          onSelect={() => setOpenModalToChangeUser(true)}
                        >
                          {t('changeUser')}
                        </DropdownMenu.Item>
                      </UserPermissionGate>
                    </DropdownMenu.Content>
                  </DropdownMenu.Root>
                </UserPermissionGate>
              )}
            </div>
          </Alert>
          {canShowChangeUserOption && (
            <ChangeUserToRetrieveSchedulePushNotificationModal
              pageId={page.id}
              postId={postId}
              open={isModalToChangeUserOpen}
              onClose={() => setOpenModalToChangeUser(false)}
              selectedUserId={schedulingInfo.additional_data?.user?.id}
            />
          )}
        </>
      )}
      <Modal
        open={isDuplicateSuccessOpen}
        onClose={() => setDuplicateSuccessOpen(false)}
        size="xs"
      >
        <Modal.Header
          title={t('postSuccessfullyDuplicated', {
            defaultValue: 'Post successfully duplicated',
          })}
        />
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setDuplicateSuccessOpen(false)}
            data-cy="post-duplicate-success-close"
          >
            {t('close')}
          </Button>
          <Link to={duplicatedPostUrl} target="_blank">
            <Button data-cy="post-duplicate-success-show-post">
              {t('postsShift.showPost', { defaultValue: 'Show post' })}
            </Button>
          </Link>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default PostScheduledInformation;
